/**
 * DocumentItemsColumnsCanvas — podgląd klocka „Pozycje dokumentu" na płótnie kreatora formatek.
 *
 * Płótno pokazuje EDYTOR UKŁADU KOLUMN (`TableLayoutEditor`): nagłówki kolumn pozycji z jednym
 * wierszem przykładowym, a przy możliwości zapisu — przeciąganie, szerokość, menu kolumny i panel
 * „Kolumny". Każda akcja wysyła jedną łatkę `settings` (`tableColumns` / `hiddenColumnIds`)
 * przez `onSettingsChange`; pełny zapis czyści starszy klucz `columnConfig`.
 *
 * Kolumny: 15 wbudowanych (bez „Akcje" — ta zawsze zostaje na końcu tabeli w edycji) oraz, gdy
 * host kreatora ma dostęp do danych tenanta (`useBuilderEnvironment().tenantData`), pola
 * dodatkowe pozycji. W kreatorze bez dostępu (panel ITE — endpointy tenanta odpowiadają 401)
 * edytor pokazuje wyłącznie kolumny wbudowane i pól nie pobiera.
 *
 * Panel „Kolumny” ma w stopce „Dodaj pole dodatkowe” (okno `AddSystemFieldModal` dla modułu
 * `document_items`) — tylko w układzie własnym, z możliwością zapisu i z dostępem do danych tenanta.
 * Kolumny, których widok odczytu nie rysuje (towar, kod, PKWiU, CN, GTIN), niosą adnotację
 * „tylko w edycji” (deskryptory).
 *
 * Układ systemowy (`systemHideOnly`) przełącza edytor w tryb `hideOnly`: sam panel „Kolumny" z
 * checkboxami, a akcja wysyła wyłącznie `{ hiddenColumnIds }` (kreator wyprowadza z tego nakładkę
 * tenanta kluczowaną typem elementu).
 *
 * Dopóki pola dodatkowe się ładują albo ich odczyt się nie udał, edytor jest PODGLĄDEM bez
 * kontrolek: zapis układu bez deskryptorów pól dodatkowych zgubiłby ich zapisaną kolejność i
 * widoczność. Błąd odczytu jest pokazany nad edytorem (nie połykamy go po cichu).
 *
 * Korzeń (`className`/`style`): `LayoutElementRegistry.applyElementStyle` klonuje zwrócony element
 * i dokłada styl elementu, więc te propsy trafiają na JEDEN stały korzeń DOM (`div`), w którym
 * komunikat błędu to opcjonalne dziecko nad edytorem — dzięki temu edytor (i otwarte okno dodawania
 * pola) nie przemontowuje się przy przejściu między stanem poprawnym a błędem odczytu pól.
 */

import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TableLayoutEditor } from '@/components/embedded-table/TableLayoutEditor';
import { useBuilderEnvironment } from '@/components/fields/builder-environment';
import type { LayoutElement } from '@/components/fields/LayoutElementRegistry';
import { AddSystemFieldModal } from '@/components/system-modules/AddSystemFieldModal';
import {
  DOCUMENT_ITEMS_LEGACY_KEYS,
  documentItemsLegacyReader,
} from '@/lib/embedded-table/document-items-descriptors';
import { useDocumentItemColumnsModel } from '@/lib/embedded-table/use-document-item-columns-model';
import { cn } from '@/lib/utils';

interface Props {
  element: LayoutElement;
  /** Zapis łatki `settings` elementu; brak = podgląd tylko do odczytu. */
  onSettingsChange?: (patch: Record<string, unknown>) => void;
  /**
   * Układ systemowy — edytor w trybie `hideOnly` (tenant tylko ukrywa kolumny; łatka
   * `{ hiddenColumnIds }`). Bez `onSettingsChange` i tak zostaje podglądem bez kontrolek.
   */
  systemHideOnly?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function DocumentItemsColumnsCanvas({
  element,
  onSettingsChange,
  systemHideOnly = false,
  className,
  style,
}: Props) {
  const { t } = useTranslation();
  const { tenantData } = useBuilderEnvironment();
  // Kreator zostaje samymi kolumnami wbudowanymi i podglądem, gdy odczyt pól się nie udał — nie
  // pokazujemy starszych pól z pamięci podręcznej (zapis układu zgubiłby ich kolejność/widoczność).
  const model = useDocumentItemColumnsModel({ withExtFields: tenantData, dropFieldsOnError: true });
  const testIdPrefix = `document-items-view-element-${element.id}`;
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  // Zapis tylko przy kompletnym zestawie kolumn (patrz nagłówek pliku).
  const canWrite = model.status === 'ready' || model.status === 'disabled';
  // Dodawanie pola dodatkowego: tylko układ własny z możliwością zapisu i tylko z dostępem do
  // danych tenanta (pola pozycji to dane tenanta — w panelu ITE endpointy odpowiadają 401).
  const canAddField = tenantData && !systemHideOnly && canWrite && onSettingsChange !== undefined;
  // OTWARTE okno żyje dłużej niż możliwość jego otwarcia: odświeżenie pól w tle może się nie udać
  // (`model.status` → `error`) w trakcie wypełniania formularza, a okno nie może wtedy zniknąć
  // razem z wpisanymi danymi. Zamykamy je dopiero, gdy znikną dane tenanta, układ stanie się
  // systemowy (tylko ukrywanie) albo zniknie zapis układu. Korekta stanu podczas renderu (wzorzec
  // Reacta dla stanu wynikającego z propsów), bez efektu i dodatkowego renderu z oknem.
  const addFieldWindowAllowed = tenantData && !systemHideOnly && onSettingsChange !== undefined;
  if (!addFieldWindowAllowed && addFieldOpen) setAddFieldOpen(false);

  const editor = (
    <TableLayoutEditor
      descriptors={model.descriptors}
      settings={element.settings}
      onChange={canWrite ? onSettingsChange : undefined}
      mode={systemHideOnly ? 'hideOnly' : 'full'}
      title={t('tenant.documents.items.paletteLabel')}
      legacy={documentItemsLegacyReader}
      legacyKeys={DOCUMENT_ITEMS_LEGACY_KEYS}
      // `min-w-0` — szeroka tabela przewija się w karcie (pasek nagłówków ma `overflow-x-auto`),
      // zamiast rozpychać rodzica.
      className="min-w-0"
      testIdPrefix={testIdPrefix}
      addColumn={
        canAddField
          ? {
              label: t('tenant.documents.items.columnLayout.addCustomField'),
              onClick: () => setAddFieldOpen(true),
            }
          : undefined
      }
    >
      {/* Okno dodawania pola (portal) zamontowane TYLKO przy otwarciu — woła `useQuery`/`useMutation`
          (wymaga `QueryClientProvider`). Zdarzenia z portalu nie docierają do kreatora: `children`
          edytora są owinięte elementem zatrzymującym bąbelkowanie.
          Sukces odświeża listę pól (`['system-module-fields', 'document_items']`), więc nowa kolumna
          pojawia się w edytorze sama (ze znaczkiem „nowa”, o ile układ jest już zapisany). */}
      {addFieldWindowAllowed && addFieldOpen && (
        <AddSystemFieldModal
          open
          onClose={() => setAddFieldOpen(false)}
          moduleSlug="document_items"
        />
      )}
    </TableLayoutEditor>
  );

  // JEDEN korzeń niezależnie od stanu odczytu pól: komunikat błędu to opcjonalne dziecko NAD edytorem
  // (stały slot), więc przejście `ready` ⇄ `error` nie przemontowuje edytora, a razem z nim otwartego
  // okna dodawania pola — inaczej błąd odświeżenia w tle zgubiłby wpisane w nim dane.
  return (
    <div className={cn('min-w-0', className)} style={style}>
      {model.status === 'error' && (
        <p
          role="alert"
          className="mb-2 text-xs text-danger-600"
          data-testid={`${testIdPrefix}-ext-fields-error`}
        >
          {t('tenant.documents.items.columnLayout.extFieldsLoadError')}
        </p>
      )}
      {editor}
    </div>
  );
}
